import organization from '@/api/system/organization';

/**
 * How often the playground build status is polled, and for how long before the
 * poll gives up with a distinct timed-out state. The elapsed bound matches
 * BuildPlaygroundJob's 900-second queue timeout: past that point the build can
 * no longer succeed, so polling further would only hammer the API.
 */
export const PLAYGROUND_POLL_INTERVAL_MS = 4000;
export const PLAYGROUND_POLL_MAX_ELAPSED_MS = 900000;

const PLAYGROUND_POLL_MAX_CONSECUTIVE_FAILURES = 3;

/**
 * The client-side playground build states. BUILDING / READY / FAILED mirror the
 * server's build_state; NONE (a 404 — the caller owns no playground), TIMED_OUT
 * (the client-side elapsed bound) and UNREACHABLE (bounded network failures)
 * are client-derived so components can map every stop condition to copy.
 */
export const PLAYGROUND_BUILD_STATES = Object.freeze({
    BUILDING: 'BUILDING',
    READY: 'READY',
    FAILED: 'FAILED',
    NONE: 'NONE',
    TIMED_OUT: 'TIMED_OUT',
    UNREACHABLE: 'UNREACHABLE',
});

const TERMINAL_BUILD_STATES = [PLAYGROUND_BUILD_STATES.READY, PLAYGROUND_BUILD_STATES.FAILED];

const emptyPlaygroundStatus = () => ({
    state: null,
    tenantId: null,
    name: null,
    slug: null,
    preset: null,
    failureReason: null,
    expiresOn: null,
    jurisdiction: null,
});

// Organisation entries are keyed by slug; the volatile poll status lives under
// this reserved key and is stripped before persisting, so a page reload never
// resurrects a stale BUILDING/READY snapshot.
const persistableOrganisations = (state) => {
    const { playgroundStatus, ...organisations } = state;

    return organisations;
};

const persist = (state) => {
    localStorage.setItem('organisation', JSON.stringify(persistableOrganisations(state)));
};

// The active poll's handle lives at module scope, not in Vuex state: it holds a
// timer id and a cancellation flag, which are not serialisable state. One
// handle means one interval, which is what makes a second start a no-op.
let activePlaygroundPoll = null;

export default {
    namespaced: true,
    state: {
        /*
        HINT: This is the structure of the state object.
        [slug] = {
                        id: '',
                        slug: '',
                        tenant: {},
                        settings: {},
                        permissions: [],
                    }
        */
    },
    getters: {
        isPlayground: (state) => (slug) => {
            return state[slug]?.is_playground === true;
        },

        playgroundPreset: (state) => (slug) => {
            return state[slug]?.playground_preset ?? null;
        },

        playgroundBuildState: (state) => (slug) => {
            return state[slug]?.playground_build_state ?? null;
        },

        playgroundStatus: (state) => {
            return state.playgroundStatus ?? emptyPlaygroundStatus();
        },
    },
    mutations: {
        setOrganisation(state, data) {
            state[data.slug] = data;

            persist(state);
        },

        loadOrganisations(state) {
            let organisation = JSON.parse(localStorage.getItem('organisation'));

            if (organisation) {
                Object.keys(organisation).forEach(function (key) {
                    state[key] = organisation[key];
                });
            }
        },

        resetOrganisation(state) {
            state = {};
        },

        // Accepts both the status endpoint's full payload and the create /
        // regenerate 202 envelope, which carries no failure_reason, expires_on
        // or jurisdiction — a partial payload must never clobber a known expiry
        // or market.
        setPlaygroundStatus(state, playground) {
            state.playgroundStatus = {
                state: playground.build_state ?? null,
                tenantId: playground.tenant_id ?? null,
                // The create/regenerate 202 envelope carries no name, so keep a
                // known one rather than blanking the heading mid-flow.
                name: playground.name ?? state.playgroundStatus?.name ?? null,
                slug: playground.slug ?? null,
                preset: playground.preset ?? null,
                failureReason: playground.failure_reason ?? null,
                expiresOn: playground.expires_on ?? state.playgroundStatus?.expiresOn ?? null,
                jurisdiction: playground.jurisdiction ?? state.playgroundStatus?.jurisdiction ?? null,
            };

            const entry = state[playground.slug];

            if (entry) {
                entry.playground_preset = playground.preset ?? null;
                entry.playground_build_state = playground.build_state ?? null;

                if (playground.expires_on !== undefined) {
                    entry.playground_expires_on = playground.expires_on;
                }

                persist(state);
            }
        },

        markPlaygroundStatusMissing(state) {
            state.playgroundStatus = {
                ...emptyPlaygroundStatus(),
                state: PLAYGROUND_BUILD_STATES.NONE,
            };
        },

        markPlaygroundStatusTimedOut(state) {
            state.playgroundStatus = {
                ...(state.playgroundStatus ?? emptyPlaygroundStatus()),
                state: PLAYGROUND_BUILD_STATES.TIMED_OUT,
            };
        },

        markPlaygroundStatusUnreachable(state) {
            state.playgroundStatus = {
                ...(state.playgroundStatus ?? emptyPlaygroundStatus()),
                state: PLAYGROUND_BUILD_STATES.UNREACHABLE,
            };
        },
    },
    actions: {
        /**
         * One-shot status read. A 404 means "the caller owns no playground" —
         * a normal state, stored as NONE and resolved as null, never surfaced
         * as an error. Every other rejection is rethrown untouched so the
         * server's message key reaches the caller.
         */
        fetchPlaygroundStatus({ commit }) {
            return organization
                .playgroundStatus()
                .then((response) => {
                    commit('setPlaygroundStatus', response.data.playground);

                    return response.data.playground;
                })
                .catch((error) => {
                    if (error?.response?.status === 404) {
                        commit('markPlaygroundStatusMissing');

                        return null;
                    }

                    return Promise.reject(error);
                });
        },

        /**
         * The bounded build-status poll. Idempotent — a second start while a
         * poll is active returns the same completion promise. Stops on a
         * terminal build state (READY/FAILED), on a 404 (NONE), on explicit
         * cancellation, at the maximum elapsed time (TIMED_OUT), or after
         * bounded consecutive network failures (UNREACHABLE). Resolves with
         * the stop reason.
         */
        pollPlaygroundStatus({ commit, dispatch }) {
            if (activePlaygroundPoll) {
                return activePlaygroundPoll.completion;
            }

            const poll = {
                cancelled: false,
                startedAt: Date.now(),
                consecutiveFailures: 0,
                timer: null,
            };

            const finish = () => {
                if (activePlaygroundPoll === poll) {
                    activePlaygroundPoll = null;
                }
            };

            poll.completion = new Promise((resolve) => {
                poll.resolve = resolve;

                const tick = async () => {
                    if (poll.cancelled) {
                        return resolve('cancelled');
                    }

                    if (Date.now() - poll.startedAt >= PLAYGROUND_POLL_MAX_ELAPSED_MS) {
                        commit('markPlaygroundStatusTimedOut');
                        finish();

                        return resolve(PLAYGROUND_BUILD_STATES.TIMED_OUT);
                    }

                    let playground = null;

                    try {
                        playground = await dispatch('fetchPlaygroundStatus');
                        poll.consecutiveFailures = 0;
                    } catch (error) {
                        poll.consecutiveFailures += 1;

                        if (poll.consecutiveFailures >= PLAYGROUND_POLL_MAX_CONSECUTIVE_FAILURES) {
                            commit('markPlaygroundStatusUnreachable');
                            finish();

                            return resolve(PLAYGROUND_BUILD_STATES.UNREACHABLE);
                        }
                    }

                    if (poll.cancelled) {
                        return resolve('cancelled');
                    }

                    if (playground === null && poll.consecutiveFailures === 0) {
                        finish();

                        return resolve(PLAYGROUND_BUILD_STATES.NONE);
                    }

                    if (playground && TERMINAL_BUILD_STATES.includes(playground.build_state)) {
                        finish();

                        return resolve(playground.build_state);
                    }

                    poll.timer = setTimeout(tick, PLAYGROUND_POLL_INTERVAL_MS);
                };

                tick();
            });

            activePlaygroundPoll = poll;

            return poll.completion;
        },

        stopPlaygroundStatusPolling() {
            if (!activePlaygroundPoll) {
                return;
            }

            const poll = activePlaygroundPoll;
            activePlaygroundPoll = null;

            poll.cancelled = true;

            if (poll.timer) {
                clearTimeout(poll.timer);
            }

            if (poll.resolve) {
                poll.resolve('cancelled');
            }
        },
    },
};
